export const ASSET_FIELD_KEYS = [
  'buildingNumber',
  'unitNumber',
  'address',
  'floor',
  'block',
  'parcel',
  'subParcel',
  'area',
  'description'
] as const;

export type AssetFieldKey = (typeof ASSET_FIELD_KEYS)[number];

/** `area` is the one non-text asset column (`numeric(12,2)`) — shared by API validation and the web form. */
export const AREA_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;

/** DEVELOPER_UNIT's only two required fields — shared by API validation and the web form. */
export const ASSET_FIELD_MAX_LENGTHS: Record<'buildingNumber' | 'unitNumber', number> = {
  buildingNumber: 50,
  unitNumber: 50
};

export interface AssetFieldSpec {
  key: AssetFieldKey;
  /** i18n key suffix, not copy — e.g. `unitNumber` reads as apartment/office/parking number per type. */
  labelToken: string;
  required: boolean;
}

/** Which rule produced a title. The caller maps it to copy, so this package stays i18n-free. */
export type AssetTitleSource = 'description' | 'developerUnit' | 'address' | 'registry';

/** A title a type can offer, usable only when every one of its fields is filled. */
export interface AssetTitleCandidate {
  source: AssetTitleSource;
  keys: AssetFieldKey[];
}

/** A type's attributes as they arrive from the DB or a DTO — every field nullable. */
export type AssetAttributes = Partial<Record<AssetFieldKey, string | null | undefined>>;

/** One segment of a display code, built from a field's value with an optional literal prefix. */
export interface AssetCodeSegment {
  key: AssetFieldKey;
  prefix?: string;
}

export interface AssetTypeDescriptor {
  /** Ordered — drives both the create form and the display label. */
  fields: AssetFieldSpec[];
  /** Ordered — the first candidate whose every field is filled titles the asset. */
  titleCandidates: AssetTitleCandidate[];
  /** Leading segment of the display code — ASCII, never localized. Absent for DEVELOPER_UNIT. */
  code?: string;
  /** Which field values compose the display code, and in what order. */
  codeSegments: AssetCodeSegment[];
  /** Whether a label leads with the type's localized name. False for DEVELOPER_UNIT — its numbers identify it. */
  hasTypeName: boolean;
}
